import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, navigationMenuTriggerStyle } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { cn } from "@/lib/utils";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="39218" className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} data-index={i} className="h-9 w-20 bg-slate-100 animate-pulse rounded-lg" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.filter(item => item.label !== "Darmowa Wycena").map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={cn(navigationMenuTriggerStyle(), "bg-transparent hover:bg-transparent focus:bg-transparent data-[active]:bg-transparent data-[state=open]:bg-transparent", "text-slate-900 font-semibold tracking-tight transition-all duration-300", "border-b-2 border-transparent hover:border-blue-600 rounded-none px-2 mx-2 h-auto py-2")}>
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="md:hidden fixed inset-x-0 top-14 bottom-0 bg-white/95 backdrop-blur-2xl border-t border-slate-100 z-[60] overflow-y-auto p-6 flex flex-col gap-6 animate-in slide-in-from-top-5 fade-in duration-300">
      <nav className="flex flex-col gap-4">
        {loading ? <div className="flex flex-col gap-4">
            {[1, 2, 3].map(i => <div key={i} data-index={i} className="h-10 w-full bg-slate-100 animate-pulse rounded-xl" />)}
          </div> : menuItems.filter(item => item.label !== "Darmowa Wycena").map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-semibold text-slate-900 hover:text-blue-600 transition-colors py-3 border-b border-slate-50" onClick={onClose}>
              {item.label}
            </Link>)}
      </nav>
      <div className="mt-auto">
        <Button className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 hover:opacity-90 rounded-xl shadow-lg shadow-blue-500/20" size="lg" asChild>
          <Link to="/ContactPage">Zamów wycenę</Link>
        </Button>
      </div>
    </div>;
}
export default function Nagłówek() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="nawigacja-hydroflush" className={cn("sticky top-0 z-50 w-full transition-all duration-500 border-b", isScrolled ? "bg-white/80 backdrop-blur-xl border-slate-200 shadow-md py-2" : "bg-white border-transparent py-4")}>
      <div className="container mx-auto px-4 flex items-center justify-between h-14">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src="https://hydroflush.pl/wp-content/uploads/2026/03/logo.png" alt="Hydroflush Logo" className="h-8 w-auto object-contain transition-transform hover:scale-125" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex flex-1 justify-center px-8">
          <MenuProvider menu_id="16">
            <DesktopMenu />
          </MenuProvider>
        </div>

        {}
        <div className="hidden md:flex flex-shrink-0">
          <Button variant="outline" className="border-blue-600/20 hover:border-blue-600/50 hover:bg-blue-50 text-blue-700 font-semibold rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 text-white border-none shadow-lg shadow-blue-500/10 hover:shadow-blue-500/20 transition-all hover:-translate-y-0.5" asChild>
            <Link to="/ContactPage">Zamów wycenę</Link>
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu" className={cn("text-slate-900 transition-all duration-300", isScrolled ? "opacity-0 pointer-events-none -translate-y-2" : "opacity-100 translate-y-0")}>
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </Button>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}